/**
 * 알림 관련 번역 텍스트
 */
import type { Language, Locale } from '@/config/locales';

export interface NotificationTranslations {
  notifications: string;
  markAllAsRead: string;
  deleteAll: string;
  noNotifications: string;
  justNow: string;
  minutesAgo: string;
  hoursAgo: string;
  daysAgo: string;
  orderReady: string;
  orderPreparing: string;
  promotion: string;
  systemAlert: string;
}

// #522 — Locale 단일 출처 정합 (15언어 전수).
export const translations:
  & Record<Language, NotificationTranslations>
  & Record<Exclude<Locale, Language>, NotificationTranslations> = {
  ko: {
    notifications: '알림',
    markAllAsRead: '모두 읽음 처리',
    deleteAll: '알림 모두 삭제',
    noNotifications: '알림이 없습니다',
    justNow: '방금 전',
    minutesAgo: '분 전',
    hoursAgo: '시간 전',
    daysAgo: '일 전',
    orderReady: '주문 준비완료',
    orderPreparing: '주문 준비중',
    promotion: '프로모션',
    systemAlert: '시스템 알림'
  },
  es: {
    notifications: 'Notificaciones',
    markAllAsRead: 'Marcar todo como leído',
    deleteAll: 'Eliminar todo',
    noNotifications: 'No hay notificaciones',
    justNow: 'Ahora mismo',
    minutesAgo: 'min. atrás',
    hoursAgo: 'h. atrás',
    daysAgo: 'd. atrás',
    orderReady: 'Pedido listo',
    orderPreparing: 'Preparando pedido',
    promotion: 'Promoción',
    systemAlert: 'Alerta del sistema'
  },
  en: {
    notifications: 'Notifications',
    markAllAsRead: 'Mark all as read',
    deleteAll: 'Delete all',
    noNotifications: 'No notifications',
    justNow: 'Just now',
    minutesAgo: 'min ago',
    hoursAgo: 'h ago',
    daysAgo: 'd ago',
    orderReady: 'Order ready',
    orderPreparing: 'Order preparing',
    promotion: 'Promotion',
    systemAlert: 'System alert'
  },
  pt: {
    notifications: 'Notificações',
    markAllAsRead: 'Marcar tudo como lido',
    deleteAll: 'Excluir tudo',
    noNotifications: 'Não há notificações',
    justNow: 'Agora mesmo',
    minutesAgo: 'min atrás',
    hoursAgo: 'h atrás',
    daysAgo: 'd atrás',
    orderReady: 'Pedido pronto',
    orderPreparing: 'Preparando pedido',
    promotion: 'Promoção',
    systemAlert: 'Alerta do sistema'
  },
  fr: {
    notifications: 'Notifications',
    markAllAsRead: 'Tout marquer comme lu',
    deleteAll: 'Tout supprimer',
    noNotifications: 'Aucune notification',
    justNow: 'À l’instant',
    minutesAgo: 'min',
    hoursAgo: 'h',
    daysAgo: 'j',
    orderReady: 'Commande prête',
    orderPreparing: 'Commande en préparation',
    promotion: 'Promotion',
    systemAlert: 'Alerte système'
  },
  zh: {
    notifications: '通知',
    markAllAsRead: '全部标为已读',
    deleteAll: '删除全部',
    noNotifications: '暂无通知',
    justNow: '刚刚',
    minutesAgo: '分钟前',
    hoursAgo: '小时前',
    daysAgo: '天前',
    orderReady: '订单已备好',
    orderPreparing: '订单准备中',
    promotion: '促销',
    systemAlert: '系统通知'
  },
  ja: {
    notifications: '通知',
    markAllAsRead: 'すべて既読にする',
    deleteAll: 'すべて削除',
    noNotifications: '通知はありません',
    justNow: 'たった今',
    minutesAgo: '分前',
    hoursAgo: '時間前',
    daysAgo: '日前',
    orderReady: '注文の準備完了',
    orderPreparing: '注文を準備中',
    promotion: 'プロモーション',
    systemAlert: 'システム通知'
  },
  hi: {
    notifications: 'सूचनाएं',
    markAllAsRead: 'सभी को पढ़ा हुआ चिह्नित करें',
    deleteAll: 'सभी हटाएं',
    noNotifications: 'कोई सूचना नहीं',
    justNow: 'अभी-अभी',
    minutesAgo: 'मिनट पहले',
    hoursAgo: 'घंटे पहले',
    daysAgo: 'दिन पहले',
    orderReady: 'ऑर्डर तैयार है',
    orderPreparing: 'ऑर्डर तैयार किया जा रहा है',
    promotion: 'प्रमोशन',
    systemAlert: 'सिस्टम अलर्ट'
  },
  de: {
    notifications: 'Benachrichtigungen',
    markAllAsRead: 'Alle als gelesen markieren',
    deleteAll: 'Alle löschen',
    noNotifications: 'Keine Benachrichtigungen',
    justNow: 'Gerade eben',
    minutesAgo: 'Min.',
    hoursAgo: 'Std.',
    daysAgo: 'T.',
    orderReady: 'Bestellung fertig',
    orderPreparing: 'Bestellung wird zubereitet',
    promotion: 'Aktion',
    systemAlert: 'Systembenachrichtigung'
  },
  it: {
    notifications: 'Notifiche',
    markAllAsRead: 'Segna tutto come letto',
    deleteAll: 'Elimina tutto',
    noNotifications: 'Nessuna notifica',
    justNow: 'Proprio ora',
    minutesAgo: 'min fa',
    hoursAgo: 'h fa',
    daysAgo: 'g fa',
    orderReady: 'Ordine pronto',
    orderPreparing: 'Ordine in preparazione',
    promotion: 'Promozione',
    systemAlert: 'Avviso di sistema'
  },
  ru: {
    notifications: 'Уведомления',
    markAllAsRead: 'Отметить все как прочитанные',
    deleteAll: 'Удалить все',
    noNotifications: 'Нет уведомлений',
    justNow: 'Только что',
    minutesAgo: 'мин. назад',
    hoursAgo: 'ч. назад',
    daysAgo: 'дн. назад',
    orderReady: 'Заказ готов',
    orderPreparing: 'Заказ готовится',
    promotion: 'Акция',
    systemAlert: 'Системное уведомление'
  },
  ar: {
    notifications: 'الإشعارات',
    markAllAsRead: 'وضع علامة مقروء على الكل',
    deleteAll: 'حذف الكل',
    noNotifications: 'لا توجد إشعارات',
    justNow: 'الآن',
    minutesAgo: 'دقائق مضت',
    hoursAgo: 'ساعات مضت',
    daysAgo: 'أيام مضت',
    orderReady: 'الطلب جاهز',
    orderPreparing: 'جارٍ تحضير الطلب',
    promotion: 'عرض ترويجي',
    systemAlert: 'تنبيه النظام'
  },
  vi: {
    notifications: 'Thông báo',
    markAllAsRead: 'Đánh dấu tất cả đã đọc',
    deleteAll: 'Xóa tất cả',
    noNotifications: 'Không có thông báo',
    justNow: 'Vừa xong',
    minutesAgo: 'phút trước',
    hoursAgo: 'giờ trước',
    daysAgo: 'ngày trước',
    orderReady: 'Đơn hàng đã sẵn sàng',
    orderPreparing: 'Đang chuẩn bị đơn hàng',
    promotion: 'Khuyến mãi',
    systemAlert: 'Thông báo hệ thống'
  },
  th: {
    notifications: 'การแจ้งเตือน',
    markAllAsRead: 'ทำเครื่องหมายว่าอ่านแล้วทั้งหมด',
    deleteAll: 'ลบทั้งหมด',
    noNotifications: 'ไม่มีการแจ้งเตือน',
    justNow: 'เมื่อสักครู่',
    minutesAgo: 'นาทีที่แล้ว',
    hoursAgo: 'ชั่วโมงที่แล้ว',
    daysAgo: 'วันที่แล้ว',
    orderReady: 'ออเดอร์พร้อมแล้ว',
    orderPreparing: 'กำลังเตรียมออเดอร์',
    promotion: 'โปรโมชัน',
    systemAlert: 'การแจ้งเตือนระบบ'
  },
  nl: {
    notifications: 'Meldingen',
    markAllAsRead: 'Alles als gelezen markeren',
    deleteAll: 'Alles verwijderen',
    noNotifications: 'Geen meldingen',
    justNow: 'Zojuist',
    minutesAgo: 'min geleden',
    hoursAgo: 'u geleden',
    daysAgo: 'd geleden',
    orderReady: 'Bestelling klaar',
    orderPreparing: 'Bestelling wordt bereid',
    promotion: 'Promotie',
    systemAlert: 'Systeemmelding'
  }
};
